import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Button, Badge, Alert } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { layoutModeTypes } from "../../Components/constants/layout";
import logoDark from '../../assets/images/exiaLogo_dark.png';
import logoLight from '../../assets/images/exiaLogo_dark.png';

const OAuthDebug = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [debugData, setDebugData] = useState(null);
    const [error, setError] = useState(null);

    // Layout state selector
    const selectLayoutState = (state) => state.Layout;
    const layoutData = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutModeType: layout.layoutModeType,
        })
    );
    const { layoutModeType } = useSelector(layoutData);

    useEffect(() => {
        console.log('OAuth Debug - Component mounted');
        console.log('OAuth Debug - Location state:', location.state);
        
        if (location.state) {
            setDebugData(location.state);
            console.log('OAuth Debug - Received data:', location.state);
        } else {
            setError('No OAuth data received. Please complete the OAuth flow first.');
            console.log('OAuth Debug - No data received, showing error');
        }
    }, [location.state]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('Copied to clipboard!');
        }).catch(err => {
            console.error('Failed to copy:', err);
        });
    };

    const formatToken = (token) => {
        if (!token) return 'No token';
        if (token.length > 50) {
            return token.substring(0, 50) + '...';
        }
        return token;
    };

    const decodeJWT = (token) => {
        try {
            if (!token) return null;
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('JWT decode error:', error);
            return null;
        }
    };

    const mode = layoutModeType || layoutModeTypes.LIGHTMODE;
    document.title = "OAuth Debug | SeedNations";

    if (error) {
        return (
            <ParticlesAuth>
                <div className="auth-page-content">
                    <Container>
                        <Row className="justify-content-center">
                            <Col md={8} lg={12} xl={5}>
                                <Card className="overflow-hidden">
                                    <CardBody className="p-4">
                                        <div className="text-center">
                                            <div className="mb-4">
                                                <img
                                                    src={mode === layoutModeTypes.LIGHTMODE ? logoLight : logoDark}
                                                    alt=""
                                                    height="34"
                                                />
                                            </div>
                                            <Alert color="danger">
                                                <h5>❌ OAuth Debug Error</h5>
                                                <p>{error}</p>
                                            </Alert>
                                            <div className="d-grid gap-2">
                                                <Button color="primary" onClick={() => navigate('/login/cognito')}>
                                                    Go to OAuth Login
                                                </Button>
                                                <Button color="outline-secondary" onClick={() => navigate('/home')}>
                                                    Go to Home
                                                </Button>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ParticlesAuth>
        );
    }

    if (!debugData) {
        return (
            <ParticlesAuth>
                <div className="auth-page-content">
                    <Container>
                        <Row className="justify-content-center">
                            <Col md={8} lg={12} xl={5}>
                                <Card className="overflow-hidden">
                                    <CardBody className="p-4">
                                        <div className="text-center">
                                            <div className="mb-4">
                                                <img
                                                    src={mode === layoutModeType ? logoLight : logoDark}
                                                    alt=""
                                                    height="34"
                                                />
                                            </div>
                                            <div className="py-4">
                                                <span>Loading OAuth debug data...</span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ParticlesAuth>
        );
    }

    const { access_token, refresh_token, userData, auth_method } = debugData;
    const decodedToken = decodeJWT(access_token);

    return (
        <ParticlesAuth>
            <div className="auth-page-content">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10} xl={8}>
                            <Card className="overflow-hidden">
                                <CardBody className="p-4">
                                    <div className="text-center mb-4">
                                        <div className="mb-4">
                                            <img
                                                src={mode === layoutModeTypes.LIGHTMODE ? logoLight : logoDark}
                                                alt=""
                                                height="34"
                                            />
                                        </div>
                                        <h4 className="text-success">🎉 OAuth Authentication Successful!</h4>
                                        <p className="text-muted">Debug information for OAuth verification</p>
                                        <Badge color="success" className="fs-6">
                                            Auth Method: {auth_method}
                                        </Badge>
                                    </div>

                                    <Row>
                                        <Col md={6}>
                                            <Card className="border-success">
                                                <CardBody>
                                                    <h6 className="text-success">
                                                        <i className="bx bx-key me-2"></i>
                                                        Access Token
                                                    </h6>
                                                    <div className="bg-light p-2 rounded mb-2">
                                                        <code className="small">
                                                            {formatToken(access_token)}
                                                        </code>
                                                    </div>
                                                    <Button 
                                                        size="sm" 
                                                        color="outline-success"
                                                        onClick={() => copyToClipboard(access_token)}
                                                    >
                                                        Copy Full Token
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        <Col md={6}>
                                            <Card className="border-warning">
                                                <CardBody>
                                                    <h6 className="text-warning">
                                                        <i className="bx bx-refresh me-2"></i>
                                                        Refresh Token
                                                    </h6>
                                                    <div className="bg-light p-2 rounded mb-2">
                                                        <code className="small">
                                                            {formatToken(refresh_token)}
                                                        </code>
                                                    </div>
                                                    <Button 
                                                        size="sm" 
                                                        color="outline-warning"
                                                        onClick={() => copyToClipboard(refresh_token)}
                                                    >
                                                        Copy Full Token
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Card className="border-info">
                                                <CardBody>
                                                    <h6 className="text-info">
                                                        <i className="bx bx-user me-2"></i>
                                                        User Data
                                                    </h6>
                                                    <div className="bg-light p-2 rounded">
                                                        <pre className="small mb-0">
                                                            {JSON.stringify(userData, null, 2)}
                                                        </pre>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        <Col md={6}>
                                            <Card className="border-primary">
                                                <CardBody>
                                                    <h6 className="text-primary">
                                                        <i className="bx bx-code me-2"></i>
                                                        Decoded JWT Payload
                                                    </h6>
                                                    <div className="bg-light p-2 rounded">
                                                        <pre className="small mb-0">
                                                            {decodedToken ? JSON.stringify(decodedToken, null, 2) : 'Invalid JWT'}
                                                        </pre>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Row className="mt-3">
                                        <Col>
                                            <Card className="border-warning">
                                                <CardBody>
                                                    <h6 className="text-warning">
                                                        <i className="bx bx-image me-2"></i>
                                                        Profile Photo Priority Logic
                                                    </h6>
                                                    <div className="bg-light p-2 rounded">
                                                        <div className="row">
                                                            <div className="col-md-4">
                                                                <strong>1. Google OAuth Photo:</strong>
                                                                <div className="small text-muted">
                                                                    {userData.profile_photo ? '✅ Available' : '❌ Not Available'}
                                                                </div>
                                                                {userData.profile_photo && (
                                                                    <div className="mt-1">
                                                                        <img src={userData.profile_photo} alt="Google Photo" className="rounded" style={{width: '50px', height: '50px', objectFit: 'cover'}} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>2. Existing Profile Photo:</strong>
                                                                <div className="small text-muted">
                                                                    {userData.profile_photo_url_object?.profile_url ? '✅ Available' : '❌ Not Available'}
                                                                </div>
                                                                {userData.profile_photo_url_object?.profile_url && (
                                                                    <div className="mt-1">
                                                                        <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/file/avatar/${userData.profile_photo_url_object.profile_url}`} alt="Existing Photo" className="rounded" style={{width: '50px', height: '50px', objectFit: 'cover'}} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>3. Final Selected Photo:</strong>
                                                                <div className="small text-muted">
                                                                    {userData.profile_photo || userData.profile_photo_url_object?.profile_url ? '✅ Selected' : '❌ Using Default'}
                                                                </div>
                                                                {(userData.profile_photo || userData.profile_photo_url_object?.profile_url) && (
                                                                    <div className="mt-1">
                                                                        <img src={userData.profile_photo || `${process.env.REACT_APP_API_URL?.replace('/api', '')}/file/avatar/${userData.profile_photo_url_object.profile_url}`} alt="Final Photo" className="rounded" style={{width: '50px', height: '50px', objectFit: 'cover'}} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="mt-2">
                                                            <strong>Profile Photo URL Object:</strong>
                                                            <pre className="small mb-0 mt-1">
                                                                {JSON.stringify(userData.profile_photo_url_object, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Row className="mt-3">
                                        <Col>
                                            <Card className="border-success">
                                                <CardBody>
                                                    <h6 className="text-success">
                                                        <i className="bx bx-storage me-2"></i>
                                                        SessionStorage (What Protected Routes Check)
                                                    </h6>
                                                    <div className="bg-light p-2 rounded">
                                                        <div className="row">
                                                            <div className="col-md-4">
                                                                <strong>authToken:</strong>
                                                                <div className="small text-muted">
                                                                    {sessionStorage.getItem('authToken') ? '✅ Present' : '❌ Missing'}
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>refreshToken:</strong>
                                                                <div className="small text-muted">
                                                                    {sessionStorage.getItem('refreshToken') ? '✅ Present' : '❌ Missing'}
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>user:</strong>
                                                                <div className="small text-muted">
                                                                    {sessionStorage.getItem('user') ? '✅ Present' : '❌ Missing'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="mt-2">
                                                            <strong>Protected Routes Status:</strong>
                                                            <div className={`small ${sessionStorage.getItem('authToken') ? 'text-success' : 'text-danger'}`}>
                                                                {sessionStorage.getItem('authToken') ? '✅ Accessible' : '❌ Will Redirect to Login'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Row className="mt-4">
                                        <Col>
                                            <Alert color="success">
                                                <h6>✅ OAuth Flow Complete!</h6>
                                                <p className="mb-0">
                                                    The OAuth authentication was successful. You now have valid JWT tokens 
                                                    and can access protected routes. This is a temporary debug page - 
                                                    in production, you would redirect to the main application.
                                                </p>
                                            </Alert>
                                        </Col>
                                    </Row>

                                    <div className="text-center mt-4">
                                        <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                                            <Button 
                                                color="success" 
                                                size="lg"
                                                onClick={() => navigate('/home')}
                                            >
                                                🏠 Go to Home (Continue to App)
                                            </Button>
                                            <Button 
                                                color="outline-primary" 
                                                size="lg"
                                                onClick={() => navigate('/login/cognito')}
                                            >
                                                🔄 Test OAuth Again
                                            </Button>
                                            <Button 
                                                color="outline-secondary" 
                                                size="lg"
                                                onClick={() => {
                                                    localStorage.clear();
                                                    navigate('/login');
                                                }}
                                            >
                                                🚪 Logout & Clear Tokens
                                            </Button>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </ParticlesAuth>
    );
};

export default OAuthDebug; 